import { getDatabase, ref, get } from "firebase/database";
import { app } from "../firebase/config";
import { logAdminAction } from "./databaseUtils";

const database = getDatabase(app);

/**
 * Check for duplicate email addresses across all users
 * @param {string} email - Email to check
 * @param {string} excludeUserId - User ID to exclude from check (for updates)
 * @returns {Promise<Object>} - Check result
 */
export const checkDuplicateEmail = async (email, excludeUserId = null) => {
  try {
    console.log("🔄 Checking for duplicate email:", email);

    if (!email) {
      return { isDuplicate: false, error: "Email is required" };
    }

    // OPTIMIZED: Use user_profiles instead of users
    const usersRef = ref(database, "user_profiles");
    const snapshot = await get(usersRef);

    if (!snapshot.exists()) {
      return { isDuplicate: false, error: null };
    }

    const users = snapshot.val();
    const emailLower = email.toLowerCase();

    for (const [userId, userData] of Object.entries(users)) {
      // Skip the user being updated
      if (excludeUserId && userId === excludeUserId) {
        continue;
      }

      // Check Google users
      if (userData.profile?.email?.toLowerCase() === emailLower) {
        return {
          isDuplicate: true,
          error: "Email already exists (Google user)",
          existingUserId: userId,
          authProvider: "google",
        };
      }

      // Check email auth users
      if (userData.emailAuth?.email?.toLowerCase() === emailLower) {
        return {
          isDuplicate: true,
          error: "Email already exists (Email user)",
          existingUserId: userId,
          authProvider: "email",
        };
      }
    }

    console.log("✅ No duplicate email found");
    return { isDuplicate: false, error: null };
  } catch (error) {
    console.error("❌ Error checking duplicate email:", error);
    return { isDuplicate: false, error: "Failed to check email" };
  }
};

/**
 * Generate secure user ID for email users
 * @param {string} prefix - Prefix for user ID (default: 'email')
 * @returns {string} - Unique user ID
 */
export const generateSecureUserId = (prefix = "email") => {
  const timestamp = Date.now().toString(36);
  const randomPart1 = Math.random().toString(36).substring(2, 8);
  const randomPart2 = Math.random().toString(36).substring(2, 6);

  return `${prefix}_${timestamp}_${randomPart1}_${randomPart2}`;
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {Object} - Validation result
 */
export const validateEmailFormat = (email) => {
  if (!email) {
    return { isValid: false, error: "Email is required" };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailRegex.test(email)) {
    return { isValid: false, error: "Invalid email format" };
  }

  if (email.length > 254) {
    return { isValid: false, error: "Email is too long" };
  }

  return { isValid: true, error: null };
};

/**
 * Validate display name
 * @param {string} displayName - Display name to validate
 * @returns {Object} - Validation result
 */
export const validateDisplayName = (displayName) => {
  if (!displayName || displayName.trim().length === 0) {
    return { isValid: false, error: "Display name is required" };
  }

  if (displayName.trim().length < 2) {
    return {
      isValid: false,
      error: "Display name must be at least 2 characters",
    };
  }

  if (displayName.length > 50) {
    return {
      isValid: false,
      error: "Display name is too long (max 50 characters)",
    };
  }

  // Check for invalid characters
  const invalidChars = /[<>\"'&]/;
  if (invalidChars.test(displayName)) {
    return {
      isValid: false,
      error: "Display name contains invalid characters",
    };
  }

  return { isValid: true, error: null };
};

/**
 * Activate/Deactivate email user account
 * @param {string} userId - User ID
 * @param {boolean} isActive - New active status
 * @param {string} adminId - Admin performing the action
 * @param {string} reason - Reason for status change
 * @returns {Promise<Object>} - Operation result
 */
export const toggleUserActivation = async (
  userId,
  isActive,
  adminId,
  reason = ""
) => {
  try {
    console.log(`🔄 ${isActive ? "Activating" : "Deactivating"} user:`, userId);

    if (!userId || !adminId) {
      return { success: false, error: "User ID and admin ID are required" };
    }

    // Import here to avoid circular dependency
    const { toggleEmailUserStatus } = await import(
      "../services/emailAuthService"
    );

    const result = await toggleEmailUserStatus(userId, isActive, adminId);

    if (result.success) {
      // Log admin action
      await logAdminAction(
        adminId,
        isActive ? "ACTIVATE_USER" : "DEACTIVATE_USER",
        userId,
        {
          reason: reason,
          newStatus: isActive,
        }
      );
    }

    return result;
  } catch (error) {
    console.error("❌ Error toggling user activation:", error);
    return { success: false, error: "Failed to update user status" };
  }
};

/**
 * Get user activity summary
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Activity summary
 */
export const getUserActivitySummary = async (userId) => {
  try {
    const userRef = ref(database, `users/${userId}`);
    const snapshot = await get(userRef);

    if (!snapshot.exists()) {
      return { success: false, error: "User not found", activity: null };
    }

    const userData = snapshot.val();

    // Calculate post activity
    const posts = userData.posts || {};
    const autoPostCount = posts.autoPostGenerator?.links?.length || 0;
    const normalPostCount = posts.normalPostGenerator?.links?.length || 0;
    const totalPosts = autoPostCount + normalPostCount;

    // Get membership info
    const membership = userData.membership || 0;
    const membershipExpiry = userData.membershipExpiry;
    let membershipStatus = "free";

    if (membership && membershipExpiry) {
      try {
        const expiryDate = new Date(membershipExpiry);
        const now = new Date();
        membershipStatus = expiryDate > now ? "premium" : "expired";
      } catch {
        membershipStatus = "expired";
      }
    }

    // Get auth-specific info
    const authProvider = userData.profile?.authProvider || "google";
    let lastLogin = null;
    let accountStatus = "active";

    if (authProvider === "email" && userData.emailAuth) {
      lastLogin = userData.emailAuth.lastLogin;
      accountStatus = userData.emailAuth.isActive ? "active" : "disabled";
    }

    const activity = {
      userId: userId,
      authProvider: authProvider,
      accountStatus: accountStatus,
      lastLogin: lastLogin,
      createdAt: userData.createdAt,
      totalPosts: totalPosts,
      autoPostCount: autoPostCount,
      normalPostCount: normalPostCount,
      membershipStatus: membershipStatus,
      membership: membership,
      membershipExpiry: membershipExpiry,
      profile: userData.profile || {},
    };

    return { success: true, error: null, activity };
  } catch (error) {
    console.error("❌ Error getting user activity:", error);
    return { success: false, error: error.message, activity: null };
  }
};

/**
 * Bulk operations for email users
 * @param {Array} userIds - Array of user IDs
 * @param {string} operation - Operation to perform ('activate', 'deactivate', 'delete')
 * @param {string} adminId - Admin performing the operation
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} - Bulk operation result
 */
export const bulkEmailUserOperation = async (
  userIds,
  operation,
  adminId,
  options = {}
) => {
  try {
    console.log(`🔄 Performing bulk ${operation} on ${userIds.length} users`);

    if (!userIds || userIds.length === 0) {
      return { success: false, error: "No user IDs provided", results: [] };
    }

    if (!adminId) {
      return { success: false, error: "Admin ID is required", results: [] };
    }

    const results = [];
    let successCount = 0;
    let failureCount = 0;

    for (const userId of userIds) {
      try {
        let result = { userId, success: false, error: null };

        switch (operation) {
          case "activate":
            const activateResult = await toggleUserActivation(
              userId,
              true,
              adminId,
              options.reason
            );
            result = { userId, ...activateResult };
            break;

          case "deactivate":
            const deactivateResult = await toggleUserActivation(
              userId,
              false,
              adminId,
              options.reason
            );
            result = { userId, ...deactivateResult };
            break;

          case "delete":
            // Import here to avoid circular dependency
            const { deleteUser } = await import("./databaseUtils");
            const deleteResult = await deleteUser(userId);
            if (deleteResult.success) {
              await logAdminAction(adminId, "DELETE_USER", userId, {
                reason: options.reason,
              });
            }
            result = { userId, ...deleteResult };
            break;

          default:
            result = {
              userId,
              success: false,
              error: `Unknown operation: ${operation}`,
            };
        }

        if (result.success) {
          successCount++;
        } else {
          failureCount++;
        }

        results.push(result);
      } catch (error) {
        console.error(`❌ Error processing user ${userId}:`, error);
        results.push({
          userId,
          success: false,
          error: error.message,
        });
        failureCount++;
      }
    }

    // Log bulk operation
    await logAdminAction(
      adminId,
      `BULK_${operation.toUpperCase()}`,
      "multiple",
      {
        totalUsers: userIds.length,
        successCount,
        failureCount,
        reason: options.reason,
      }
    );

    console.log(
      `✅ Bulk ${operation} completed: ${successCount} success, ${failureCount} failures`
    );

    return {
      success: true,
      error: null,
      results,
      summary: {
        total: userIds.length,
        success: successCount,
        failure: failureCount,
      },
    };
  } catch (error) {
    console.error("❌ Error in bulk operation:", error);
    return { success: false, error: error.message, results: [] };
  }
};

/**
 * Get email user creation statistics
 * @param {string} adminId - Admin ID (optional, for admin-specific stats)
 * @param {number} days - Number of days to look back (default: 30)
 * @returns {Promise<Object>} - Creation statistics
 */
export const getEmailUserCreationStats = async (adminId = null, days = 30) => {
  try {
    // OPTIMIZED: Use user_profiles instead of users
    const usersRef = ref(database, "user_profiles");
    const snapshot = await get(usersRef);

    if (!snapshot.exists()) {
      return { success: true, stats: { total: 0, byDay: [], byAdmin: {} } };
    }

    const users = snapshot.val();
    const cutoffDate = Date.now() - days * 24 * 60 * 60 * 1000;

    const emailUsers = Object.values(users).filter(
      (user) =>
        user.emailAuth &&
        user.emailAuth.createdAt >= cutoffDate &&
        (!adminId || user.emailAuth.createdBy === adminId)
    );

    // Group by day
    const byDay = {};
    const byAdmin = {};

    emailUsers.forEach((user) => {
      const createdDate = new Date(user.emailAuth.createdAt);
      const dayKey = createdDate.toISOString().split("T")[0];

      byDay[dayKey] = (byDay[dayKey] || 0) + 1;

      const createdBy = user.emailAuth.createdBy;
      byAdmin[createdBy] = (byAdmin[createdBy] || 0) + 1;
    });

    // Convert to array format for charts
    const byDayArray = Object.entries(byDay)
      .map(([date, count]) => ({
        date,
        count,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));

    return {
      success: true,
      error: null,
      stats: {
        total: emailUsers.length,
        byDay: byDayArray,
        byAdmin: byAdmin,
      },
    };
  } catch (error) {
    console.error("❌ Error getting creation stats:", error);
    return { success: false, error: error.message, stats: null };
  }
};

/**
 * Validate user creation data
 * @param {Object} userData - User data to validate
 * @returns {Object} - Validation result
 */
export const validateUserCreationData = (userData) => {
  const errors = [];

  // Validate email
  const emailValidation = validateEmailFormat(userData.email);
  if (!emailValidation.isValid) {
    errors.push(emailValidation.error);
  }

  // Validate display name
  const nameValidation = validateDisplayName(userData.displayName);
  if (!nameValidation.isValid) {
    errors.push(nameValidation.error);
  }

  // Validate password
  if (!userData.password || userData.password.length < 8) {
    errors.push("Password must be at least 8 characters long");
  }

  // Validate admin ID
  if (!userData.adminId) {
    errors.push("Admin ID is required");
  }

  // Validate membership (if provided)
  if (userData.membership !== undefined && userData.membership !== null) {
    const membership = parseInt(userData.membership);
    if (isNaN(membership) || membership < 0 || membership > 4) {
      errors.push("Invalid membership level");
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
